import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { useEffect, useState } from 'react';
import { View, Pressable, Platform } from 'react-native';
import Label from '../common/Label';
import useTranslation from '../../hooks/translation-hook';
import useCustomTheme from '../../hooks/theme-hook';
import CustomButton from '../common/CustomButton';
import { useAppSelector } from '../../hooks/store-hook';
import { getAuth, RecaptchaVerifier, multiFactor, PhoneAuthProvider, sendEmailVerification, PhoneMultiFactorGenerator } from 'firebase/auth';
import CustomTextInput, { InputTypeValidation, ValidationResult } from '../common/CustomTextInput';
import { useDispatch } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import useApi from '../../hooks/api-hook';
import { MysteryShopperClient } from '../../api/api.g';
import ReauthenticateUserForm from './ReauthenticateUserForm';
import { userPrefsActions } from '../../store/userPrefsSlice';
import Loading from '../common/Loading';
import useUserPrefsService from '../../hooks/userPrefsService-hook';
import { UserPrefsHelper } from '../../models/helpers/userPrefsHelper';
import { getCanadianPhoneValidationState, toCanadianPhoneE164 } from '../../helpers/phoneNumberHelper';

export interface AccountVerificationPopupProps {
  onClose?(): void;
  changingPhoneNumber?: boolean;
  emailVerified?: boolean;
}

const AccountVerificationPopup = (props: AccountVerificationPopupProps) => {
  const t = useTranslation();
  const theme = useCustomTheme();
  const popupFieldError = theme.isDark ? (theme.text.error?.toString() ?? '#ff9bb4') : (theme.colorErrorPrimary?.toString() ?? '#8e183d');
  const auth = getAuth();
  const userPrefs = useAppSelector((selector) => selector.userPrefs);
  const authState = useAppSelector((selector) => selector.auth);
  const mysteryShopperApi = useApi(MysteryShopperClient);
  const dispatch = useDispatch();
  const userPrefService = useUserPrefsService();
  const popupBackgroundColor = theme.background.modal?.toString() ?? '#e9f5f3';
  const popupTitleColor = theme.text.darkTurquoiseTwo?.toString() ?? '#035164';
  const popupBodyColor = theme.text.primary?.toString() ?? '#1a1818';
  const popupButtonColor = theme.background.darkTurquoise?.toString() ?? '#035164';
  const popupTitleStyle = { fontSize: 28, color: popupTitleColor, fontWeight: '600' as const, width: '100%' as const, flexShrink: 0 };
  const popupBodyStyle = {
    marginTop: 12,
    fontSize: 18,
    color: popupBodyColor,
    fontWeight: '400' as const,
    width: '100%' as const,
    flexShrink: 0,
  };
  const popupBodyEmphasisStyle = { ...popupBodyStyle, fontWeight: '600' as const };
  const popupHelperTextStyle = { ...popupBodyStyle, marginTop: 10, fontSize: 16, opacity: 0.9 };
  const popupButtonInfoStyle = {
    marginTop: 12,
    fontSize: 14,
    color: popupBodyColor,
    fontWeight: '500' as const,
    width: '100%' as const,
    textAlign: 'center' as const,
    opacity: 0.85,
  };
  const popupButtonStyle = { marginTop: 24, backgroundColor: popupButtonColor, borderColor: popupButtonColor };
  const normalizeVerificationCode = (value: string) => value.replace(/\D/g, '').slice(0, 6);
  const getVerificationCodeErrorMessage = (error: any) => {
    const errorMessage = error?.message ?? '';

    if (error?.code === 'auth/invalid-verification-code') {
      return t('Invalid-verification-code');
    }

    if (error?.code === 'auth/second-factor-already-in-use' || errorMessage.includes('SECOND_FACTOR_EXISTS')) {
      return t('PhoneNumber-used');
    }

    return t('Something-went-wrong-please-try-again-later');
  };

  const [isLoading, setIsLoading] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [emailVerified, setEmailVerified] = useState(false);
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);
  const [showVerifyPhone, setShowVerifyPhone] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const [verificationId, setVerificationId] = useState(null);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [reloginError, setReloginError] = useState(false);
  const [phoneRequired, setPhoneRequired] = useState(false);
  const [verificationCodeError, setVerificationCodeError] = useState<string | undefined>(undefined);
  const [showReauthenticateUser, setShowReauthenticateUser] = useState(false);
  const [resendEmailTimer, setResendEmailTimer] = useState(0);
  const [newPhone, setNewPhone] = useState(undefined);
  const [showNewPhone, setShowNewPhone] = useState(false);
  const [userPrefsValue, setUserPrefsValue] = useState(userPrefs);
  const [validation, setValidation] = useState<ValidationResult>();

  useEffect(() => {
    let interval = setInterval(() => {
      if (userPrefs.lastSendEmailTime) {
        const diff = new Date().getTime() - userPrefs.lastSendEmailTime.getTime();
        // round to seconds
        let nextResendEmailTimer = Math.round((120000 - diff) / 1000);
        if (nextResendEmailTimer <= 0) {
          clearInterval(interval);
          nextResendEmailTimer = 0;
        }
        setResendEmailTimer(nextResendEmailTimer);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [userPrefs.lastSendEmailTime]);

  const verifyPhoneNumber = useMutation({
    mutationFn: (phoneNumber: string) => {
      setIsLoading(true);
      return mysteryShopperApi
        .verifyPhoneNumber(phoneNumber, mysteryShopperApi.tokenSource?.token)
        .then((response) => {
          if (response) {
            setIsLoading(false);
            setValidation({ valid: false, message: t('PhoneNumber-used') });
          } else {
            setShowNewPhone(false);
            sendCode();
            setShowVerifyPhone(true);
          }
        })
        .catch(() => {
          setIsLoading(false);
          setValidation({ valid: false, message: "Error: Couldn't verify phone number." });
        });
    },
  });

  useEffect(() => {
    let interval;

    if (props.emailVerified || (!props.changingPhoneNumber && authState.user.emailVerified && (showIntro || showVerifyEmail))) {
      setShowIntro(false);
      setShowVerifyEmail(false);
      setEmailVerified(true);
    }

    if (showVerifyEmail) {
      interval = setInterval(async () => {
        if (authState.user) {
          await authState.user.reload();
          const updatedUser = auth.currentUser;

          if (updatedUser.emailVerified) {
            setShowIntro(false);
            setShowVerifyEmail(false);
            setEmailVerified(true);
            clearInterval(interval);
          }
        }
      }, 30000); // Check every minute
    }

    // Cleanup subscription and interval on unmount
    return () => {
      clearInterval(interval);
    };
  }, [authState.user, showVerifyEmail, showIntro]);

  useEffect(() => {
    const recaptchaContainerId = '2fa-recaptcha-container-2fa-popup';
    let createdWebContainer: HTMLElement | null = null;
    let verifierTarget: string | HTMLElement = recaptchaContainerId;

    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      let webContainer = document.getElementById(recaptchaContainerId) as HTMLElement | null;

      if (!webContainer) {
        webContainer = document.createElement('div');
        webContainer.id = recaptchaContainerId;
        Object.assign(webContainer.style, {
          position: 'fixed',
          right: '16px',
          bottom: '16px',
          zIndex: '5000',
          width: '256px',
          minHeight: '60px',
        });
        document.body.appendChild(webContainer);
        createdWebContainer = webContainer;
      }

      verifierTarget = webContainer;
    }

    const verifier = new RecaptchaVerifier(auth, verifierTarget as any, {
      size: 'invisible',
      badge: 'bottomright',
      callback: (response) => {},
    });

    setRecaptchaVerifier(verifier);

    return () => {
      try {
        verifier.clear();
      } catch {
        // no-op: verifier cleanup is best-effort on web
      }

      if (createdWebContainer?.parentNode) {
        createdWebContainer.parentNode.removeChild(createdWebContainer);
      }
    };
  }, []);

  const verifyPhoneMutation = useMutation({
    mutationFn: () => {
      return mysteryShopperApi.verifyAccount();
    },
    onSuccess() {
      setShowVerifyPhone(false);
      setPhoneVerified(true);
      dispatch(userPrefsActions.setPhoneNumberVerified(true));
    },
  });

  const sendCode = () => {
    const normalizedPhoneNumber = toCanadianPhoneE164(newPhone ?? userPrefs.phoneNumber);

    if (!normalizedPhoneNumber) {
      setValidation(getCanadianPhoneValidationState(newPhone ?? userPrefs.phoneNumber, t, { required: true }));
      return;
    }

    setIsLoading(true);

    multiFactor(auth.currentUser)
      .getSession()
      .then(function (multiFactorSession) {
        const phoneInfoOptions = {
          phoneNumber: normalizedPhoneNumber,
          session: multiFactorSession,
        };

        const phoneAuthProvider = new PhoneAuthProvider(auth);
        phoneAuthProvider
          .verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier)
          .then(function (verificationId) {
            setIsLoading(false);
            setVerificationId(verificationId);
          })
          .catch((error) => {
            setIsLoading(false);
            if (error.code == 'auth/requires-recent-login') {
              setShowVerifyPhone(false);
              setReloginError(true);
            } else {
              setVerificationCodeError(t('Unable-to-send-the-verification-code-Please-try-ag'));
            }
          });
      })
      .catch(() => {
        setIsLoading(false);
        setVerificationCodeError(t('Unable-to-send-the-verification-code-Please-try-ag'));
      });
  };

  const handleResendCode = () => {
    setCodeSent(true);
    setVerificationCodeError(undefined);
    sendCode();
  };

  const handleVerifyCode = () => {
    if (isLoading) {
      return;
    }

    if (!verificationId) {
      setVerificationCodeError(t('Unable-to-send-the-verification-code-Please-try-ag'));
      return;
    }

    if (verificationCode.length !== 6) {
      setVerificationCodeError(t('Invalid-verification-code'));
      return;
    }

    setVerificationCodeError(undefined);

    let multiFactorAssertion;

    try {
      const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
      multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
    } catch (error) {
      setVerificationCodeError(getVerificationCodeErrorMessage(error));
      return;
    }

    setIsLoading(true);

    if (props.changingPhoneNumber) {
      const data = UserPrefsHelper.mapToSaveBasicInformationDto(userPrefsValue);
      mysteryShopperApi.saveBasicInformation(data).then(() => {
        userPrefService.updateUserProfileInfo((userPrefs) => {
          const mfa = multiFactor(auth.currentUser);
          if (mfa?.enrolledFactors[0]) {
            mfa.unenroll(mfa.enrolledFactors[0]).then(() => {
              mfa
                .enroll(multiFactorAssertion, 'User Phone Number')
                .then(() => {
                  setShowVerifyPhone(false);
                  setPhoneVerified(true);
                })
                .catch((error) => {
                  setVerificationCodeError(getVerificationCodeErrorMessage(error));
                })
                .finally(() => {
                  setIsLoading(false);
                });
            });
          } else {
            mfa
              .enroll(multiFactorAssertion, 'User Phone Number')
              .then(() => {
                setShowVerifyPhone(false);
                setPhoneVerified(true);
              })
              .catch((error) => {
                setVerificationCodeError(getVerificationCodeErrorMessage(error));
              })
              .finally(() => {
                setIsLoading(false);
              });
          }
        });
      });
    } else {
      multiFactor(auth.currentUser)
        .enroll(multiFactorAssertion, 'User Phone Number')
        .then(() => {
          verifyPhoneMutation.mutate();
        })
        .catch((error) => {
          setVerificationCodeError(getVerificationCodeErrorMessage(error));
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleOnClosePress = () => {
    props.onClose && props.onClose();
  };

  const handleResendEmailVerification = () => {
    dispatch(userPrefsActions.setLastSendEmailTime(new Date()));

    sendEmailVerification(auth.currentUser);
  };

  const handleStartVerification = () => {
    if (authState.user.emailVerified) {
      setShowIntro(false);

      if (props.changingPhoneNumber) {
        setShowReauthenticateUser(true);
      } else {
        setEmailVerified(true);
      }
    } else {
      dispatch(userPrefsActions.setLastSendEmailTime(new Date()));
      sendEmailVerification(auth.currentUser);
      setShowIntro(false);
      setShowVerifyEmail(true);
    }
  };

  const handleReauthenticateUser = () => {
    if (userPrefs.phoneNumber) {
      setEmailVerified(false);
      setReloginError(true);
    } else {
      setEmailVerified(false);
      setPhoneRequired(true);
    }
  };

  const handleStartPhoneVerification = () => {
    if (props.changingPhoneNumber) {
      setShowReauthenticateUser(false);
      setShowNewPhone(true);
    } else {
      setShowReauthenticateUser(false);
      sendCode();
      setShowVerifyPhone(true);
    }
  };

  const handleNewPhoneSubmit = () => {
    const nextValidation = getCanadianPhoneValidationState(userPrefsValue.phoneNumber, t, { required: true });

    setValidation(nextValidation);

    if (!nextValidation.valid) {
      return;
    }

    verifyPhoneNumber.mutate(userPrefsValue.phoneNumber);
  };

  return (
    <View style={{ backgroundColor: popupBackgroundColor, width: 350, height: 465, paddingTop: 12 }}>
      {Platform.OS !== 'web' && <View id="2fa-recaptcha-container-2fa-popup" style={{ width: 0, height: 0 }} />}
      {showIntro && (
        <Pressable style={{ position: 'absolute' as any, top: 20, right: 20 }} onPress={handleOnClosePress}>
          <FontAwesomeIcon icon={faXmark} size={30} color={theme.text.darkTurquoiseTwo.toString()} />
        </Pressable>
      )}
      {showIntro && (
        <View style={{ flexGrow: 1, flexShrink: 1, marginTop: 38, paddingHorizontal: 16, paddingBottom: 16 }}>
          <View style={{ display: 'flex', flexGrow: 1, flexShrink: 1 }}>
            <Label style={popupTitleStyle} numberOfLines={3}>
              {t('Secure-Your-Account')}
            </Label>
            <Label style={popupBodyStyle} numberOfLines={10}>
              {props.changingPhoneNumber ? t('We-need-to-verify-your-account-before-you-can-chan') : t('Lets-verify-your-email-and-enable-two-factor-authe')}
            </Label>
          </View>
          <CustomButton
            style={popupButtonStyle}
            textStyle={{ color: 'white' }}
            title={t('Lets-Do-It')}
            onPress={handleStartVerification}
          />
        </View>
      )}
      {showVerifyEmail && (
        <View style={{ flexGrow: 1, flexShrink: 1, marginTop: 8, paddingHorizontal: 16, paddingBottom: 16 }}>
          <View style={{ display: 'flex', flexGrow: 1, flexShrink: 1 }}>
            <Label style={popupTitleStyle} numberOfLines={3}>
              {t('Verify-Your-Email')}
            </Label>
            <Label style={popupBodyStyle} numberOfLines={10}>
              {t('We-have-sent-a-verification-email-to')}
            </Label>
            <Label style={popupBodyEmphasisStyle} numberOfLines={10}>
              {authState.user.email}
            </Label>
            <Label style={popupHelperTextStyle} numberOfLines={10}>
              {t('You-will-receive-an-email-with-a-link-to-confirm-y')}
            </Label>
          </View>
          <CustomButton
            disabled={resendEmailTimer > 0}
            disabledPatternOpacity={0}
            style={popupButtonStyle}
            disabledStyle={{ backgroundColor: popupButtonColor, borderColor: popupButtonColor, opacity: 0.55 }}
            textStyle={{ color: 'white' }}
            disabledTextStyle={{ color: 'white' }}
            title={t('Resend')}
            onPress={handleResendEmailVerification}
          />
          {resendEmailTimer > 0 && (
            <Label style={popupButtonInfoStyle} numberOfLines={2}>
              {t('Resend-in', { seconds: resendEmailTimer.toString() })}
            </Label>
          )}
        </View>
      )}
      {emailVerified && (
        <View style={{ flexGrow: 1, flexShrink: 1, marginTop: 8, paddingHorizontal: 16, paddingBottom: 16 }}>
          <View style={{ display: 'flex', flexGrow: 1, flexShrink: 1 }}>
            <Label style={popupTitleStyle} numberOfLines={3}>
              {t('Your-email-has-been-verified')}
            </Label>
            <Label style={popupBodyStyle} numberOfLines={10}>
              {t('Now-lets-enable-two-factor-authentication-2FA-for')}
            </Label>
          </View>
          <CustomButton
            style={popupButtonStyle}
            textStyle={{ color: 'white' }}
            title={t('continue')}
            onPress={handleReauthenticateUser}
          />
        </View>
      )}
      {phoneRequired && (
        <View style={{ flexGrow: 1, flexShrink: 1, marginTop: 8, paddingHorizontal: 16, paddingBottom: 16 }}>
          <View style={{ display: 'flex', flexGrow: 1, flexShrink: 1 }}>
            <Label style={popupTitleStyle} numberOfLines={3}>
              {t('Phone-Number-Required')}
            </Label>
            <Label style={popupBodyStyle} numberOfLines={10}>
              {t('Please-add-your-phone-number-in-the-profile-settin')}
            </Label>
          </View>
          <CustomButton
            style={popupButtonStyle}
            textStyle={{ color: 'white' }}
            title={t('close')}
            onPress={handleOnClosePress}
          />
        </View>
      )}
      {reloginError && (
        <View style={{ flexGrow: 1, flexShrink: 1, marginTop: 8, paddingHorizontal: 16, paddingBottom: 16 }}>
          <View style={{ display: 'flex', flexGrow: 1, flexShrink: 1 }}>
            <Label style={popupTitleStyle} numberOfLines={3}>
              {t('Reauthenticate')}
            </Label>
            <Label style={popupBodyStyle} numberOfLines={10}>
              {t('You-will-need-to-log-in-again-to-activate-the-two')}
            </Label>
          </View>
          <CustomButton
            style={popupButtonStyle}
            textStyle={{ color: 'white' }}
            title={t('continue')}
            onPress={() => {
              setReloginError(false);
              setShowReauthenticateUser(true);
            }}
          />
        </View>
      )}
      {showReauthenticateUser && <ReauthenticateUserForm onReauthenticate={handleStartPhoneVerification} />}
      {showNewPhone && (
        <View style={{ flexGrow: 1, flexShrink: 1, marginTop: 8, paddingHorizontal: 16, paddingBottom: 16 }}>
          <View style={{ display: 'flex', flexGrow: 1, flexShrink: 1 }}>
            <Label style={{ ...popupBodyStyle, marginBottom: 16 }} numberOfLines={3}>
              {t('Please-enter-your-new-phone-number')}
            </Label>
            <CustomTextInput
              placeholder={t('your_cellphone_number')}
              forcePlaceholderTextColor={!theme.isDark ? '#454545' : undefined}
              style={!theme.isDark ? { color: '#000000' } : undefined}
              required
              validateOnBlur
              showErrorMessage
              hasError={validation?.valid === false}
              errorMessage={validation?.valid === false ? validation.message : undefined}
              errorTextStyle={{ color: popupFieldError }}
              value={newPhone}
              inputTypeValidation={InputTypeValidation.phone}
              onChangeText={(t) => {
                setNewPhone(t);
                setValidation(undefined);
                setUserPrefsValue((current) => {
                  return { ...current, phoneNumber: t };
                });
              }}
              onValidationChange={(validation) => {
                setValidation(validation);
              }}
              returnKeyType="done"
              onSubmitEditing={() => {
                handleNewPhoneSubmit();
              }}
            />
          </View>
          <CustomButton
            style={popupButtonStyle}
            textStyle={{ color: 'white' }}
            title={t('continue')}
            onPress={() => {
              handleNewPhoneSubmit();
            }}
          />
        </View>
      )}
      {showVerifyPhone && (
        <>
          {isLoading ? (
            <Loading />
          ) : (
            <View style={{ flexGrow: 1, flexShrink: 1, marginTop: 8, paddingHorizontal: 16, paddingBottom: 16 }}>
              <View style={{ display: 'flex', flexGrow: 1, flexShrink: 1 }}>
                <Label style={popupTitleStyle} numberOfLines={3}>
                  {t('Enter-code')}
                </Label>
                <Label style={popupBodyStyle} numberOfLines={10}>
                  {t('We-have-sent-a-verification-code-to')}
                </Label>
                <Label style={popupBodyEmphasisStyle} numberOfLines={10}>
                  {`+1 ${newPhone ?? userPrefs.phoneNumber}`}
                </Label>
              </View>
              <CustomTextInput
                style={{ marginTop: 24 }}
                placeholder={t('Verification-Code')}
                keyboardType="number-pad"
                maxLength={6}
                showErrorMessage
                hasError={!!verificationCodeError}
                errorMessage={verificationCodeError}
                errorTextStyle={{ color: popupFieldError }}
                onChangeText={(value) => {
                  setVerificationCode(normalizeVerificationCode(value));
                  setVerificationCodeError(undefined);
                }}
                value={verificationCode}
                returnKeyType="done"
                onSubmitEditing={() => {
                  if (verificationCode.length == 6) {
                    handleVerifyCode();
                  }
                }}
              />
              {verificationCode.length == 6 ? (
                <CustomButton
                  style={{ ...popupButtonStyle, opacity: isLoading ? 0.5 : 1 }}
                  textStyle={{ color: 'white' }}
                  title={t('Verify-Code')}
                  onPress={handleVerifyCode}
                />
              ) : (
                <CustomButton
                  style={{ ...popupButtonStyle, opacity: codeSent ? 0.5 : 1 }}
                  textStyle={{ color: 'white' }}
                  title={codeSent ? t('Code-sent') : t('Resend-Code')}
                  onPress={handleResendCode}
                />
              )}
            </View>
          )}
        </>
      )}
      {phoneVerified && (
        <View style={{ flexGrow: 1, flexShrink: 1, marginTop: 8, paddingHorizontal: 16, paddingBottom: 16 }}>
          <View style={{ display: 'flex', flexGrow: 1, flexShrink: 1 }}>
            <Label style={popupTitleStyle} numberOfLines={3}>
              {t('Your-phone-number-has-been-verified')}
            </Label>
            <Label style={popupBodyStyle} numberOfLines={10}>
              {props.changingPhoneNumber ? t('Your-phone-number-has-been-successfully-changed') : t('You-have-successfully-enabled-two-factor-authentic')}
            </Label>
          </View>
          <CustomButton
            style={popupButtonStyle}
            textStyle={{ color: 'white' }}
            title={t('Done')}
            onPress={() => {
              props.onClose && props.onClose();
            }}
          />
        </View>
      )}
    </View>
  );
};

export default AccountVerificationPopup;
